import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorResponse } from 'src/app/models/error.model';
import { TipoFianza } from 'src/app/models/tipo-fianza.model';
import { TipoDescuento } from 'src/app/models/tipo-descuento.model';
import { Zona } from 'src/app/models/zona.model';
import { TiposFianzaService } from 'src/app/services/tipos-fianza.service';
import { TiposDescuentoService } from 'src/app/services/tipos-descuento.service';
import { ZonasService } from 'src/app/services/zonas.service';
import { UtilsService } from 'src/app/services/utils.service';

@Component({
    selector: 'app-edit-tipo-fianza',
    templateUrl: './edit-tipo-fianza.component.html',
    styleUrls: ['./edit-tipo-fianza.component.scss']
})
export class EditTipoFianzaComponent implements OnInit {

    tipoFianza?: TipoFianza;
    zonas: Zona[] = [];
    tiposDescuento: TipoDescuento[] = [];

    readonly claveOpciones = [
        { value: 'basica',        label: 'Básica — Alquiler / Expensa' },
        { value: 'con_abl',       label: 'Con ABL — Alquiler / Expensa / ABL' },
        { value: 'con_servicios', label: 'Con Servicios — Alquiler / Expensa / ABL / Servicios' },
        { value: 'completa',      label: 'Completa — Alquiler / Expensa / ABL / Servicios / IKE' },
    ];

    form = new UntypedFormGroup({
        nombre:                 new UntypedFormControl('', [Validators.required]),
        clave:                  new UntypedFormControl(null, [Validators.required]),
        tipoProducto:           new UntypedFormControl('VIVIENDA', [Validators.required]),
        plazoMinMeses:          new UntypedFormControl(1, [Validators.required, Validators.min(1)]),
        plazoMaxMeses:          new UntypedFormControl(72, [Validators.required, Validators.min(1)]),
        coeficientePrimaMensual: new UntypedFormControl(null, [Validators.required]),
        comision:               new UntypedFormControl(null, [Validators.required]),
        idsTipoDescuento:       new UntypedFormControl([]),
        idZona:                 new UntypedFormControl(null),
        activo:                 new UntypedFormControl(true, [Validators.required])
    });

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private tiposFianzaService: TiposFianzaService,
        private tiposDescuentoService: TiposDescuentoService,
        private zonasService: ZonasService,
        private utilsService: UtilsService
    ) { }

    ngOnInit(): void {
        this.loadZonas();
        this.loadTiposDescuento();
        this.route.params.subscribe(params => {
            if (params?.id) {
                this.tiposFianzaService.getTipoFianzaById(params.id).subscribe({
                    next: tf => {
                        this.tipoFianza = tf;
                        this.form.setValue({
                            nombre:                  tf.nombre,
                            clave:                   tf.clave,
                            tipoProducto:            tf.tipoProducto,
                            plazoMinMeses:           tf.plazoMinMeses,
                            plazoMaxMeses:           tf.plazoMaxMeses,
                            coeficientePrimaMensual: tf.coeficientePrimaMensual,
                            comision:                tf.comision,
                            idsTipoDescuento:        tf.idsTipoDescuento ?? [],
                            idZona:                  tf.idZona ?? null,
                            activo:                  tf.activo
                        });
                    },
                    error: (error: ErrorResponse) => this.utilsService.alert('Error al obtener el tipo de fianza', error.error?.descripcion)
                });
            }
        });
    }

    loadZonas() {
        this.zonasService.getZonas(0, 200, [], { 'ocultar-inactivos': 'false' }).subscribe({
            next: response => { this.zonas = response.body ?? []; }
        });
    }

    get zonaLabel(): string {
        const id = this.tipoFianza?.idZona;
        if (!id) return 'Default';
        return this.zonas.find(z => z.id === id)?.nombre ?? `Zona ${id}`;
    }

    loadTiposDescuento() {
        this.tiposDescuentoService.getTiposDescuento(0, 200, [], { 'ocultar-inactivos': 'false' }).subscribe({
            next: response => { this.tiposDescuento = response.body ?? []; }
        });
    }

    save() {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }
        const v = this.form.value;
        const payload: TipoFianza = {
            nombre:                  v.nombre,
            clave:                   v.clave,
            tipoProducto:            v.tipoProducto,
            plazoMinMeses:           v.plazoMinMeses,
            plazoMaxMeses:           v.plazoMaxMeses,
            coeficientePrimaMensual: v.coeficientePrimaMensual,
            comision:                v.comision,
            idsTipoDescuento:        v.idsTipoDescuento || [],
            idZona:                  v.idZona || null,
            activo:                  v.activo
        };
        if (this.tipoFianza) {
            this.tiposFianzaService.updateTipoFianza({ ...payload, id: this.tipoFianza.id }).subscribe({
                next: () => {
                    this.utilsService.alert('Tipo de fianza actualizado', `"${payload.nombre}" fue actualizado correctamente.`);
                    this.navigateBack();
                },
                error: (error: ErrorResponse) => this.utilsService.alert('Error al actualizar', error.error?.descripcion)
            });
        } else {
            this.tiposFianzaService.createTipoFianza(payload).subscribe({
                next: () => {
                    this.utilsService.alert('Tipo de fianza creado', `"${payload.nombre}" fue creado correctamente.`);
                    this.navigateBack();
                },
                error: (error: ErrorResponse) => this.utilsService.alert('Error al crear', error.error?.descripcion)
            });
        }
    }

    cancel() {
        this.navigateBack();
    }

    private navigateBack() {
        this.router.navigate(['configuracion', 'calculadora', 'tipos-fianza']);
    }
}
